import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { api, fullMoney } from "@/lib/api";
import { Shell } from "@/components/Shell";

const STEPS = ["Business", "Quality factors", "EBITDA add-backs"];

const Label = ({ children }) => (
  <p className="font-mono-fin text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9A93AD]">{children}</p>
);

const Money = ({ id, value, onChange, testid }) => {
  const [raw, setRaw] = useState(value === 0 ? "" : String(value));
  return (
    <input
      id={id}
      data-testid={testid}
      inputMode="numeric"
      value={raw}
      placeholder="0"
      onChange={(e) => {
        const clean = e.target.value.replace(/[^0-9.-]/g, "");
        setRaw(clean);
        onChange(clean === "" ? 0 : Number(clean));
      }}
      onBlur={() => setRaw(raw === "" ? "" : String(Number(raw)))}
      className="w-full bg-transparent border-b border-[#262336] focus:border-[#E1306C] outline-none font-display text-4xl font-black text-white py-3 transition-colors duration-200"
    />
  );
};

const Choice = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-3" data-testid={testid}>
    {options.map((o) => (
      <button
        key={String(o.value)}
        type="button"
        data-testid={`${testid}-${String(o.value)}`}
        onClick={() => onChange(o.value)}
        className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-[background-color,color,border-color,box-shadow] duration-200 ${
          value === o.value
            ? "grad-bg text-white border-transparent shadow-[0_6px_22px_rgba(225,48,108,0.35)]"
            : "bg-[#1C1930] text-[#B4ADC6] border-[#262336] hover:border-[#E1306C] hover:text-white"
        }`}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const YN = [{ label: "Yes", value: true }, { label: "No", value: false }];

export default function NewValuation() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    business_name: "",
    industry: "HVAC",
    annual_revenue: 0,
    net_income: 0,
    can_run_without_owner: false,
    customer_concentration: 20,
    recurring_revenue_pct: 40,
    growth_rate: "5-10%",
    has_management_team: false,
    margin_quality: "At",
    owns_ip: false,
    owner_salary: 0,
    discretionary_expenses: 0,
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get("/industries").then(({ data }) => setMeta(data)).catch(() => toast.error("Failed to load industries"));
  }, []);

  const adjEbitda = Number(f.net_income) + Number(f.owner_salary) + Number(f.discretionary_expenses);

  const submit = async () => {
    if (!f.business_name.trim()) return toast.error("Business name is required");
    setSaving(true);
    try {
      const { data } = await api.post("/valuations", f);
      toast.success(data.summary);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not calculate valuation");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell>
      <div className="max-w-3xl" data-testid="valuation-form">
        <p className="font-mono-fin text-[11px] uppercase tracking-[0.28em] text-[#FF6B35]">
          Step {step + 1} of 3 · {STEPS[step]}
        </p>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight text-[#F3F0FA]">
          {step === 0 && "Tell us about the business"}
          {step === 1 && "The factors that move your multiple"}
          {step === 2 && "Rebuild your earnings"}
        </h1>

        <div className="mt-10 flex gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className={`h-[3px] flex-1 ${i <= step ? "grad-bg" : "bg-[#262336]"} transition-colors duration-300`} />
          ))}
        </div>

        <div className="mt-12 bg-[#141220]/90 backdrop-blur-xl border border-[#262336] rounded-[26px] p-8 md:p-12 shadow-[0_16px_60px_rgba(0,0,0,0.6)]">
          {step === 0 && (
            <div className="space-y-12 rise">
              <div>
                <Label>Business name</Label>
                <input
                  data-testid="input-business-name"
                  value={f.business_name}
                  onChange={(e) => set("business_name", e.target.value)}
                  placeholder="Northgate Mechanical"
                  className="mt-3 w-full bg-transparent border-b border-[#262336] focus:border-[#E1306C] outline-none font-display text-3xl font-black text-white py-3 transition-colors duration-200"
                />
              </div>
              <div>
                <Label>Industry</Label>
                <select
                  data-testid="select-industry"
                  value={f.industry}
                  onChange={(e) => set("industry", e.target.value)}
                  className="mt-3 w-full bg-[#1C1930] border border-[#262336] focus:border-[#E1306C] outline-none text-base font-semibold text-white px-4 py-3.5 rounded-[16px] transition-colors duration-200"
                >
                  {(meta?.industries || []).map((i) => (
                    <option key={i.name} value={i.name}>
                      {i.name} · baseline {i.baseline_multiple}x
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid sm:grid-cols-2 gap-10">
                <div>
                  <Label>Annual revenue</Label>
                  <Money testid="input-revenue" value={f.annual_revenue} onChange={(v) => set("annual_revenue", v)} />
                </div>
                <div>
                  <Label>Net income (or EBITDA)</Label>
                  <Money testid="input-net-income" value={f.net_income} onChange={(v) => set("net_income", v)} />
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-12 rise">
              <div>
                <Label>Can the business run 90+ days without you?</Label>
                <div className="mt-4">
                  <Choice testid="choice-owner" options={YN} value={f.can_run_without_owner} onChange={(v) => set("can_run_without_owner", v)} />
                </div>
              </div>
              <div>
                <Label>Largest customer as % of revenue</Label>
                <p className="mt-3 font-mono-fin text-3xl font-bold text-white" data-testid="value-concentration">
                  {f.customer_concentration}%
                </p>
                <Slider
                  data-testid="slider-concentration"
                  className="mt-5"
                  value={[f.customer_concentration]}
                  max={100}
                  step={1}
                  onValueChange={(v) => set("customer_concentration", v[0])}
                />
                <p className="mt-3 text-sm text-[#9A93AD]">Above 25% triggers a concentration discount.</p>
              </div>
              <div>
                <Label>% of revenue that is recurring / contracted</Label>
                <p className="mt-3 font-mono-fin text-3xl font-bold text-white" data-testid="value-recurring">
                  {f.recurring_revenue_pct}%
                </p>
                <Slider
                  data-testid="slider-recurring"
                  className="mt-5"
                  value={[f.recurring_revenue_pct]}
                  max={100}
                  step={1}
                  onValueChange={(v) => set("recurring_revenue_pct", v[0])}
                />
                <p className="mt-3 text-sm text-[#9A93AD]">80%+ earns a premium of +0.5x.</p>
              </div>
              <div>
                <Label>YoY revenue growth</Label>
                <div className="mt-4">
                  <Choice
                    testid="choice-growth"
                    options={(meta?.growth_options || []).map((g) => ({ label: g, value: g }))}
                    value={f.growth_rate}
                    onChange={(v) => set("growth_rate", v)}
                  />
                </div>
              </div>
              <div>
                <Label>Do you have a management team beyond yourself?</Label>
                <div className="mt-4">
                  <Choice testid="choice-management" options={YN} value={f.has_management_team} onChange={(v) => set("has_management_team", v)} />
                </div>
              </div>
              <div>
                <Label>Gross margin vs industry average</Label>
                <div className="mt-4">
                  <Choice
                    testid="choice-margin"
                    options={["Above", "At", "Below"].map((m) => ({ label: m, value: m }))}
                    value={f.margin_quality}
                    onChange={(v) => set("margin_quality", v)}
                  />
                </div>
              </div>
              <div>
                <Label>Do you own IP, patents or trade secrets?</Label>
                <div className="mt-4">
                  <Choice testid="choice-ip" options={YN} value={f.owns_ip} onChange={(v) => set("owns_ip", v)} />
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-12 rise">
              <div className="grid sm:grid-cols-2 gap-10">
                <div>
                  <Label>Owner compensation (your salary)</Label>
                  <Money testid="input-owner-salary" value={f.owner_salary} onChange={(v) => set("owner_salary", v)} />
                </div>
                <div>
                  <Label>Discretionary or one time expenses</Label>
                  <Money testid="input-discretionary" value={f.discretionary_expenses} onChange={(v) => set("discretionary_expenses", v)} />
                </div>
              </div>
              <div className="border-t border-[#262336] pt-10">
                <Label>Adjusted EBITDA</Label>
                <p className="mt-4 font-display text-5xl font-black tracking-tight grad-text" data-testid="preview-adjusted-ebitda">
                  {fullMoney(adjEbitda)}
                </p>
                <p className="mt-4 font-mono-fin text-xs text-[#9A93AD]">
                  net income plus owner salary plus one time expenses
                </p>
              </div>
            </div>
          )}

          <div className="mt-14 flex items-center justify-between border-t border-[#262336] pt-8">
            <button
              type="button"
              data-testid="form-back-button"
              disabled={step === 0}
              onClick={() => setStep((s) => s - 1)}
              className="inline-flex items-center gap-2 text-sm text-[#9A93AD] disabled:opacity-30 hover:text-[#E1306C] transition-colors duration-200"
            >
              <ArrowLeft size={16} /> Back
            </button>
            {step < 2 ? (
              <button
                type="button"
                data-testid="form-next-button"
                onClick={() => setStep((s) => s + 1)}
                className="grad-btn inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white text-sm font-bold"
              >
                Continue <ArrowRight size={16} />
              </button>
            ) : (
              <button
                type="button"
                data-testid="form-submit-button"
                disabled={saving}
                onClick={submit}
                className="grad-btn inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white text-sm font-bold disabled:opacity-60"
              >
                <Check size={16} /> {saving ? "Calculating…" : "Calculate valuation"}
              </button>
            )}
          </div>
        </div>
      </div>
    </Shell>
  );
}
